import { useQuery } from '@tanstack/react-query';
import { portalGet } from '@/lib/portal/customerClient';
import { usePortalAuthStore } from '@/stores/portalAuth.store';

export interface PortalHomeAlert {
  kind: 'estimate' | 'invoice';
  id: string;
  title: string;
  amount: number;
  dueAt: string | null;
}

export interface PortalHomeVisit {
  appointmentId: string;
  serviceName: string | null;
  status: string;
  startAt: string | null;
  scheduledDate: string | null;
  timezone: string;
  arrivalWindow: { start: string; end: string } | null;
  /** A visit with no day yet: the days (date-only, inclusive) it will fall within. */
  period: { firstDay: string; lastDay: string | null } | null;
  technicianName: string | null;
  technician: {
    name: string;
    role: string | null;
    tenureYears: number | null;
    credentials: string[];
  } | null;
  covered: boolean;
  serviceAddress: {
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null;
}

export interface PortalHomeEquipment {
  total: number;
  needAttention: number;
  underWarranty: number;
}

export interface PortalHomeMembership {
  id: string;
  planName: string | null;
  status: string;
  nextBillingDate: string | null;
  /** Given on a free, office-only option — it ends on contractEndDate. */
  isComplimentary?: boolean;
  contractEndDate?: string | null;
}

export interface PortalHomeResult {
  greetingName: string;
  needsAttention: PortalHomeAlert[];
  nextVisit: PortalHomeVisit | null;
  equipment: PortalHomeEquipment;
  membership: PortalHomeMembership | null;
}

/** The signed-in contact's home dashboard (needs-attention + next visit + stats). */
export function usePortalHome() {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'home', token],
    queryFn: () => portalGet<PortalHomeResult>('/api/portal/home'),
    enabled: Boolean(token),
    retry: false,
  });
}
